import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useResolvePlace, useReverseGeocode } from './useMaps';
import { useResolveLocale } from './useLocale';
import { useUpdateProfileAddress } from './useProfile';
import { useLocationStore, isSufficientLocation } from '../store/useLocationStore';
import type { LocationSource } from '../store/useLocationStore';
import { useAuthStore } from '../store/useAuthStore';
import { useProfileStore } from '../store/useProfileStore';
import type { Address } from '../types/address';
import type { Locale } from '../services/localeService';

interface UseAddressCaptureOptions {
  /** Called after an address is successfully captured + persisted. */
  onCaptured?: (address: Address) => void;
  /**
   * The locale the captured address is expected to fall in (the order gate
   * passes the dish's locale id). When set, the city/ZIP the user searches is
   * resolved to a locale first; if it differs from this id, the address is held
   * instead of persisted and `onSearchLocaleMismatch` fires so the caller can
   * confirm with the user before committing (see `confirmPendingSearch` /
   * `cancelPendingSearch`).
   */
  expectedLocaleId?: string | null;
  /**
   * Fired when a search resolves to a locale other than `expectedLocaleId`. The
   * resolved (entered) locale is passed for messaging; nothing is persisted
   * until `confirmPendingSearch` is called.
   */
  onSearchLocaleMismatch?: (resolvedLocale: Locale) => void;
  /**
   * Whether this capture writes the user's saved PROFILE (home) address.
   * Browse location and the profile home address are entirely separate concepts
   * (see useLocationStore) — so the profile is touched ONLY when the user is
   * explicitly setting it.
   *
   *  - `'browse'` (default): write the captured coords to the location STORE only.
   *    Browse surfaces (home feed prompt, dish-card distance gate, device guard,
   *    TopBar switcher) use this — they change where the user is browsing, never
   *    their saved address.
   *  - `'profileAddress'`: ALSO mirror to `profile.metadata.addresses[type:'profile']`
   *    — a full street when one resolves, else a partial (postal/country +
   *    state/city) that satisfies `hasOrderableProfileAddress`. Used only by the
   *    order gate, the one surface where the user is setting their home address.
   */
  mirrorMode?: 'browse' | 'profileAddress';
  /**
   * Reject a capture whose geocoded country is set and not `'US'`. When true,
   * a non-US search/coords surfaces as an inline `searchError`/`geoError` and nothing
   * gets persisted to the location store or the profile. Opt-in per call site:
   * the home-feed gate (`InlineLocationPrompt`) sets this so a non-US user
   * doesn't end up with an unresolvable address that wedges the home feed.
   * Other surfaces (order gate, distance gate, silent startup capture) leave
   * it off — they're only reachable once a valid US location is already in
   * play, so the extra check would just add latency without preventing a real
   * problem.
   */
  usOnly?: boolean;
}

/**
 * Capture a street address via device geolocation or a typed search (a ZIP,
 * or a city picked from region autocomplete).
 *
 * Geocodes the input to a full Address and writes it into the location store's
 * `location` slot (browse location). By default it touches NOTHING on the
 * profile — browse location and the saved home address are separate. Only the
 * order gate opts into `mirrorMode: 'profileAddress'` to also save the user's
 * home/tax address.
 *
 * Used by the first-time home-feed prompt, the dish-card distance gate, the
 * device guard, and the order gate — anywhere we resolve where the eater is.
 *
 * For a locale-only switch that doesn't resolve a street (the TopBar search
 * form) use `useLocaleCapture`.
 *
 * Error strings come from the `location` i18n namespace.
 */
export function useAddressCapture({
  onCaptured,
  expectedLocaleId,
  onSearchLocaleMismatch,
  mirrorMode = 'browse',
  usOnly = false,
}: UseAddressCaptureOptions = {}) {
  const { t } = useTranslation('location');
  const setLocation = useLocationStore((s) => s.setLocation);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const activeProfile = useProfileStore((s) => s.activeProfile);

  const reverseGeocodeMutation = useReverseGeocode();
  // Resolves a ZIP *or* a city name; a city's centroid gets its postal code
  // backfilled by reverse-geocode so `isSufficientLocation` still holds.
  const resolvePlaceMutation = useResolvePlace();
  const resolveLocaleMutation = useResolveLocale();
  const updateProfileAddressMutation = useUpdateProfileAddress();

  // Address from a search submit that resolved to a locale other than the
  // dish's, held until the user confirms the mismatch (confirmPendingSearch).
  const pendingSearchAddressRef = useRef<Address | null>(null);

  const [query, setQuery] = useState('');
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoError, setGeoError] = useState('');
  const [searchError, setSearchError] = useState('');
  // Separate from the device-geolocation state so a "use these coords" button
  // (e.g. the order gate's "use this dish's location") has its own spinner/error.
  const [coordsLoading, setCoordsLoading] = useState(false);
  const [coordsError, setCoordsError] = useState('');

  // `source` records how the coords were obtained — only `'device'` (browser
  // geolocation) unlocks distance display downstream. ZIP / city / dish-center
  // coords are `'postal'` (too coarse to promise an accurate distance).
  const persistLocation = async (address: Address, source: LocationSource) => {
    setLocation(address, source);

    // Profile mirror is opt-in (order gate only). Browse captures default to
    // 'browse' and never touch the saved home address — the two are independent.
    if (mirrorMode === 'profileAddress' && isAuthenticated && activeProfile) {
      try {
        const profileAddress = address.street1
          ? {
              // Full street address.
              type: 'profile' as const,
              street1: address.street1,
              street2: address.street2,
              city: address.city,
              stateProvince: address.stateProvince,
              postalCode: address.postalCode,
              country: address.country,
            }
          : {
              // Area-level: a partial `type:'profile'` entry (postal/country +
              // state/city for display) satisfies hasOrderableProfileAddress.
              // No street/coords — publish (isFullProfileAddress) still prompts
              // for a real street.
              type: 'profile' as const,
              city: address.city,
              stateProvince: address.stateProvince,
              postalCode: address.postalCode,
              country: address.country,
            };
        await updateProfileAddressMutation.mutateAsync({
          profileId: activeProfile.id,
          address: profileAddress,
        });
      } catch {
        // Non-fatal — the local store is already set, so the surface that
        // depends on a resolved location proceeds. The user can retry the
        // profile patch later from settings.
      }
    }

    onCaptured?.(address);
  };

  const handleUseDeviceLocation = () => {
    if (!navigator.geolocation) {
      setGeoError(t('errors.unsupported'));
      return;
    }

    setGeoError('');
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        reverseGeocodeMutation.mutate(
          { lat: pos.coords.latitude, lon: pos.coords.longitude },
          {
            onSuccess: async (address) => {
              if (!isSufficientLocation(address)) {
                setGeoError(t('errors.noPostalCode'));
                setGeoLoading(false);
                return;
              }
              if (usOnly && address.country && address.country !== 'US') {
                setGeoError(t('errors:COUNTRY_NOT_SUPPORTED'));
                setGeoLoading(false);
                return;
              }
              await persistLocation(address, 'device');
              setGeoLoading(false);
            },
            onError: () => {
              setGeoError(t('errors.lookupFailed'));
              setGeoLoading(false);
            },
          },
        );
      },
      (err) => {
        setGeoLoading(false);
        if (err.code === err.PERMISSION_DENIED) {
          setGeoError(t('errors.permissionDenied'));
        } else {
          setGeoError(t('errors.geoFailed'));
        }
      },
      // maximumAge: 0 — never accept a cached fix. A non-zero window lets the
      // browser return a position from before the user turned device/OS
      // location OFF (the site permission stays 'granted', so nothing else
      // catches it), which would persist a bogus locationSource: 'device' and
      // light the crosshair with no live fix. Forcing a fresh acquisition makes
      // getCurrentPosition error when location is actually off, so we never
      // enter that state. "Use my current location" must mean *current*.
      { enableHighAccuracy: false, timeout: 10_000, maximumAge: 0 },
    );
  };

  // Resolve + persist a location from known coordinates (reverse geocode). Used
  // by the order gate's "use this dish's location" option (the dish's obfuscated
  // center). Reverse-geocode is a public endpoint, so this works logged-out too.
  const captureFromCoords = (lat: number, lon: number) => {
    setCoordsError('');
    setCoordsLoading(true);
    reverseGeocodeMutation.mutate(
      { lat, lon },
      {
        onSuccess: async (address) => {
          if (!isSufficientLocation(address)) {
            setCoordsError(t('errors.lookupFailed'));
            setCoordsLoading(false);
            return;
          }
          // Adopting the dish's location for tax only — keep just postal +
          // country (state/city for the label). Deliberately drop the dish's
          // street and lat/lon: it isn't the orderer's real address and we
          // don't want to pin their feed/distance to the dish. With no street1
          // and the order gate's `profileAddress` mirror mode, persistLocation
          // saves this as a partial `type:'profile'` address for tax.
          const minimal: Address = {
            postalCode: address.postalCode,
            country: address.country,
            city: address.city,
            stateProvince: address.stateProvince,
          };
          // Dish-center coords — not the user's device, so 'postal'.
          await persistLocation(minimal, 'postal');
          setCoordsLoading(false);
        },
        onError: () => {
          setCoordsError(t('errors.lookupFailed'));
          setCoordsLoading(false);
        },
      },
    );
  };

  // `text` is the exact string to resolve — a typed ZIP, or the full
  // description of a city the user picked from autocomplete ("Denver, CO,
  // USA"). Taken as an argument rather than read off `query` because on a
  // suggestion pick the state update hasn't flushed yet.
  const handleSearchSubmit = async (text: string) => {
    setSearchError('');

    const trimmed = text.trim();
    if (!trimmed) {
      setSearchError(t('errors.searchRequired'));
      return;
    }

    let address: Address;
    try {
      address = await resolvePlaceMutation.mutateAsync(trimmed);
    } catch {
      setSearchError(t('errors.searchUnresolved'));
      return;
    }

    if (!isSufficientLocation(address)) {
      setSearchError(t('errors.searchUnresolved'));
      return;
    }
    if (usOnly && address.country && address.country !== 'US') {
      setSearchError(t('errors:COUNTRY_NOT_SUPPORTED'));
      return;
    }
    // Order gate: resolve the entered location's locale and, if it differs from
    // the dish's, hold the address and let the caller confirm the mismatch
    // before committing. A resolve failure is non-fatal — fall through and
    // persist rather than block the user on a lookup error.
    if (expectedLocaleId) {
      try {
        const resolved = await resolveLocaleMutation.mutateAsync(
          address.lat != null && address.lon != null
            ? { lat: address.lat, lon: address.lon }
            : { postalCode: address.postalCode },
        );
        if (resolved.id !== expectedLocaleId) {
          pendingSearchAddressRef.current = address;
          onSearchLocaleMismatch?.(resolved);
          return;
        }
      } catch {
        // ignore — persist below
      }
    }
    await persistLocation(address, 'postal');
  };

  // Persist a searched address that was held back on a locale mismatch, after
  // the user confirms they know the dish is in a different location.
  const confirmPendingSearch = async () => {
    const address = pendingSearchAddressRef.current;
    if (!address) return;
    pendingSearchAddressRef.current = null;
    await persistLocation(address, 'postal');
  };

  // Discard a held address (user cancelled the mismatch confirmation).
  const cancelPendingSearch = () => {
    pendingSearchAddressRef.current = null;
  };

  return {
    query,
    setQuery,
    geoLoading,
    geoError,
    searchError,
    coordsLoading,
    coordsError,
    handleUseDeviceLocation,
    captureFromCoords,
    handleSearchSubmit,
    confirmPendingSearch,
    cancelPendingSearch,
    searchSubmitting: resolvePlaceMutation.isPending || resolveLocaleMutation.isPending,
  };
}
